import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

type ReservationVoteItem = CrudArgs & { relatedProperties?: string[] };

export class ReservationVoter extends ContextVoter implements ContextVoterInterface {
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationReservationCreateany,
      contextAttribute: PermissionAttributesEnum.ContextOperationReservationCreate,
    });
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this.hasReservationAccess(
      item,
      PermissionAttributesEnum.GlobalOperationReservationReadany,
      PermissionAttributesEnum.ContextOperationReservationRead,
    );
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.hasReservationAccess(
      item,
      PermissionAttributesEnum.GlobalOperationReservationDeleteany,
      PermissionAttributesEnum.ContextOperationReservationDelete,
    );
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this.hasReservationAccess(
      item,
      PermissionAttributesEnum.GlobalOperationReservationUpdateany,
      PermissionAttributesEnum.ContextOperationReservationUpdate,
    );
  };

  public canCrud: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationReservationCreateany,
        PermissionAttributesEnum.GlobalOperationReservationReadany,
        PermissionAttributesEnum.GlobalOperationReservationUpdateany,
        PermissionAttributesEnum.GlobalOperationReservationDeleteany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextOperationReservationCreate,
        PermissionAttributesEnum.ContextOperationReservationRead,
        PermissionAttributesEnum.ContextOperationReservationUpdate,
        PermissionAttributesEnum.ContextOperationReservationDelete,
      ],
    });
  };

  private hasReservationAccess(
    item: ReservationVoteItem,
    globalAttribute: PermissionAttributesEnum,
    contextAttribute: PermissionAttributesEnum,
  ): boolean {
    if (this.permissionService.getGlobalPermissionForUser({ attribute: globalAttribute })) {
      return true;
    }

    if (item.relatedProperties?.length) {
      for (const propertyIri of item.relatedProperties) {
        const permission = this.permissionService.getScopePermissionForUser({
          iri: propertyIri,
          attribute: contextAttribute,
          scope: PermissionScope.PROPERTY,
        });

        if (permission !== null) {
          return true;
        }
      }
    }

    return this.hasAccess({
      "@id": item["@id"],
      "location": item.location,
      "client": item.client,
      "attribute": contextAttribute,
    });
  }
}
